import ReactGA from 'react-ga';
import { useEffect, useRef } from 'react';
import { isIosCordovaWebView, isWebComponentFlow } from './commonCode';
import { wcApiUrl } from '../WebComponents/commonComponents/customUtils';

const ANALYTICS_KEY = 'DS_APP_ANALYTICS';
const getAnalyticsState = () => {
  if (!window[ANALYTICS_KEY]) {
    window[ANALYTICS_KEY] = {
      gaInitialized: false,
      gaReady: false,
      gtmInitialized: false,
      reactGaInitialized: false,
    };
  }

  return window[ANALYTICS_KEY];
};

const Analytics = (props) => {
  const baseContext = isWebComponentFlow();
  const { initiateUiResponse, setGoogleCID, environment, wcFlow } = baseContext;
  const gaReadyRef = useRef(false);
  const isCancelledRef = useRef(false);
  const analyticsState = getAnalyticsState();

  const getGATrackingID = () =>
    wcFlow
      ? wcApiUrl(environment).REACT_APP_GA_TRACKING_ID[environment]
      : initiateUiResponse?.REACT_APP_GA_TRACKING_ID;

  const getGTMTrackingID = () =>
    wcFlow
      ? wcApiUrl(environment).REACT_APP_GTM_ID[environment]
      : initiateUiResponse?.REACT_APP_GTM_ID;

  const envCondition = wcFlow
    ? environment === 'dev' || environment === 'prod'
    : initiateUiResponse?.REACT_APP_ENVIRONMENT === 'production' ||
      initiateUiResponse?.REACT_APP_ENVIRONMENT === 'development';

  const initGA = (id) => {
    if (!id || analyticsState.gaInitialized) {
      return;
    }

    analyticsState.gaInitialized = true;
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    }
    if (typeof window.gtag !== 'function') {
      window.gtag = gtag;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    script.onload = () => {
      window.gtag('js', new Date());
      window.gtag('config', id);
      analyticsState.gaReady = true;
      gaReadyRef.current = true;
    };
    script.onerror = () => {
      analyticsState.gaInitialized = false;
      analyticsState.gaReady = false;
      gaReadyRef.current = false;
    };
    document.head.appendChild(script);
  };

  const initGTM = ({ gtmId, dataLayerName = 'dataLayer' }) => {
    if (!gtmId || analyticsState.gtmInitialized) {
      return;
    }

    analyticsState.gtmInitialized = true;
    window[dataLayerName] = window[dataLayerName] || [];
    window[dataLayerName].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}${
      dataLayerName !== 'dataLayer' ? `&l=${dataLayerName}` : ''
    }`;
    script.onerror = () => {
      analyticsState.gtmInitialized = false;
    };
    document.head.appendChild(script);
  };

  const captureIosCID = () => {
    console.log('[Analytics][iOS CID] Starting fallback');

    // 1. Try _ga cookie again
    const gaCookie = document.cookie.split('; ').find((cookie) => cookie.startsWith('_ga='));

    if (gaCookie) {
      const cookieValue = decodeURIComponent(gaCookie.substring('_ga='.length));
      const parts = cookieValue.split('.');

      if (parts.length >= 4) {
        const clientId = `${parts[2]}.${parts[3]}`;

        console.log('[Analytics][iOS CID] Found via cookie');

        setGoogleCID(clientId);
        return;
      }
    }

    console.log('[Analytics][iOS CID] Cookie unavailable');

    // 2. Try window.ga.getAll()
    try {
      if (typeof window.ga === 'function' && typeof window.ga.getAll === 'function') {
        const trackers = window.ga.getAll();

        const tracker = trackers.find((item) => item.get('trackingId') === getGATrackingID());

        if (tracker) {
          const clientId = tracker.get('clientId');

          if (clientId) {
            console.log('[Analytics][iOS CID] Found via window.ga.getAll()');

            setGoogleCID(clientId);
            return;
          }
        }

        console.log('[Analytics][iOS CID] window.ga.getAll() did not return CID');
      } else {
        console.log('[Analytics][iOS CID] window.ga.getAll() unavailable');
      }
    } catch (error) {
      console.log('[Analytics][iOS CID] window.ga.getAll() failed', error);
    }

    // 3. Try gtag get
    if (typeof window.gtag === 'function') {
      let completed = false;

      console.log('[Analytics][iOS CID] Trying gtag get');

      const timeout = setTimeout(() => {
        if (!completed) {
          completed = true;
          console.log('[Analytics][iOS CID] gtag get returned no CID');
          console.log('[Analytics][iOS CID] CID could not be extracted');
        }
      }, 2000);

      try {
        window.gtag('get', getGATrackingID(), 'client_id', (clientId) => {
          if (completed) {
            return;
          }

          completed = true;
          clearTimeout(timeout);

          if (clientId) {
            console.log('[Analytics][iOS CID] Found via gtag get');
            setGoogleCID(clientId);
            return;
          }

          console.log('[Analytics][iOS CID] gtag get returned empty CID');
          console.log('[Analytics][iOS CID] CID could not be extracted');
        });
      } catch (error) {
        if (!completed) {
          completed = true;
          clearTimeout(timeout);
          console.log('[Analytics][iOS CID] gtag get failed');
          console.log('[Analytics][iOS CID] CID could not be extracted');
        }
      }
    } else {
      console.log('[Analytics][iOS CID] gtag unavailable');
      console.log('[Analytics][iOS CID] CID could not be extracted');
    }
  };

  const captureCID = () => {
    let attempts = 0;
    const maxAttempts = 100;

    const getCIDFromCookie = () => {
      const gaCookie = document.cookie.split('; ').find((cookie) => cookie.startsWith('_ga='));

      if (!gaCookie) {
        return null;
      }

      const cookieValue = decodeURIComponent(gaCookie.substring('_ga='.length));
      const parts = cookieValue.split('.');

      if (parts.length < 4) {
        return null;
      }

      return `${parts[2]}.${parts[3]}`;
    };

    const checkCID = () => {
      if (isCancelledRef.current) return;

      attempts += 1;

      const clientId = getCIDFromCookie();

      if (clientId) {
        console.log(`[Analytics][CID] Found via cookie in ${attempts} attempts`);
        setGoogleCID(clientId);
        return;
      }

      if (attempts < maxAttempts) {
        setTimeout(checkCID, 150);
        return;
      }
      if (isIosCordovaWebView()) {
        const tryCaptureCID = () => {
          if (!gaReadyRef.current) {
            setTimeout(tryCaptureCID, 50);
            return;
          }
          captureIosCID();
        };
        tryCaptureCID();
      } else {
        console.log('Not an IOS Cordova');
      }
    };
    checkCID();
  };

  useEffect(() => {
    isCancelledRef.current = false;

    if (getGATrackingID()) {
      initGA(getGATrackingID());
    }
    if (envCondition) {
      // Replaced react-gtm-module to avoid its inline GTM bootstrap script, which violates CSP.
      initGTM({
        gtmId: getGTMTrackingID(),
      });

      const tryInitReactGA = () => {
        if (isCancelledRef.current) return;
        if (analyticsState.gaReady) {
          gaReadyRef.current = true;
        }

        if (!gaReadyRef.current) {
          setTimeout(tryInitReactGA, 50);
          return;
        }

        if (!analyticsState.reactGaInitialized) {
          ReactGA.initialize(getGATrackingID());
          analyticsState.reactGaInitialized = true;
        }
        ReactGA.pageview(window.location.pathname + window.location.search);
      };
      tryInitReactGA();
    }

    captureCID();

    return () => {
      isCancelledRef.current = true;
    };
  }, [initiateUiResponse]);

  return null;
};

export default Analytics;
